import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Expand, FileText, Image, FileSpreadsheet, FileCode, Upload, Loader2 } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { toast } from 'sonner';
import { notify } from '@/services/notificationService';
import { useAuth } from '@/contexts/AuthContext';
import { AITaskProcessingDialog } from '@/components/ai/ai-task-processing-dialog';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import { previewDocument, getFileType, createFullScreenPreview, readTextFile } from '../../../js/external-js/variableModules/downloadFromSDK.module';
import { auth, firebaseConfig, storageRef } from '../../../js/external-js/config/main_config';
import ExcelViewer from 'excel-viewer';
import * as mammoth from 'mammoth';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';

// DocxViewer component to handle DOCX rendering
interface DocxViewerProps {
  fileName: string;
  previewData: any;
  containerRef: string;
  loadingRef: string;
}

// Note: Only declare this interface once to avoid TS merged-declaration conflicts

// Styles for document previews
const docStyles = `
.docx-content h1 { font-size: 24px; margin-top: 24px; margin-bottom: 16px; font-weight: bold; }
.docx-content h2 { font-size: 20px; margin-top: 20px; margin-bottom: 14px; font-weight: bold; }
.docx-content h3 { font-size: 18px; margin-top: 18px; margin-bottom: 12px; font-weight: bold; }
.docx-content p { margin-bottom: 16px; line-height: 1.5; }
.docx-content ul, .docx-content ol { margin-left: 20px; margin-bottom: 16px; }
.docx-content li { margin-bottom: 8px; }
.docx-content table { border-collapse: collapse; margin-bottom: 16px; width: 100%; }
.docx-content table, .docx-content th, .docx-content td { border: 1px solid #ddd; padding: 8px; }

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #3b82f6;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

const DocxViewer: React.FC<DocxViewerProps> = ({ fileName, previewData, containerRef, loadingRef }) => {
  useEffect(() => {
    const renderDocx = async () => {
      console.log('DocxViewer effect running with previewData:', previewData);

      if (!previewData) {
        console.log('No preview data available yet');
        return;
      }

      try {
        // Get container elements
        const container = document.getElementById(containerRef);
        const loadingElement = document.getElementById(loadingRef);

        if (!container) {
          console.error('DOCX container not found:', containerRef);
          return;
        }

        console.log('Container found, loading DOCX data');

        // Get document data - either from blob or URL
        let arrayBuffer;

        if (previewData.blob) {
          console.log('Using blob to get ArrayBuffer');
          arrayBuffer = await previewData.blob.arrayBuffer();
        } else if (previewData.url) {
          console.log('Fetching from URL:', previewData.url);
          const response = await fetch(previewData.url);
          const blob = await response.blob();
          arrayBuffer = await blob.arrayBuffer();
        } else {
          throw new Error('No blob or URL available for DOCX file');
        }

        console.log('ArrayBuffer created, size:', arrayBuffer.byteLength);

        // Convert DOCX to HTML
        console.log('Converting DOCX to HTML in inline view');
        const result = await mammoth.convertToHtml({ arrayBuffer });

        // Hide loading indicator
        if (loadingElement) {
          loadingElement.style.display = 'none';
        }

        // Display the HTML content
        const contentDiv = document.createElement('div');
        contentDiv.innerHTML = result.value;
        contentDiv.className = 'docx-content';
        container.appendChild(contentDiv);

        console.log('DOCX content rendered successfully');

        // Log any warnings
        if (result.messages.length > 0) {
          console.log('DOCX conversion warnings:', result.messages);
        }
      } catch (error) {
        console.error('Error rendering DOCX:', error);
        const container = document.getElementById(containerRef);
        if (container) {
          container.innerHTML = `
            <div class="flex flex-col items-center justify-center h-full">
              <p class="text-red-500">Failed to load document</p>
              <a href="${previewData?.url || '#'}" download="${fileName}" class="text-blue-500 underline mt-2">Download instead</a>
            </div>
          `;
        }
      }
    };

    renderDocx();

    return () => {
      // Clean up if needed
      const container = document.getElementById(containerRef);
      if (container) {
        const content = container.querySelector('.docx-content');
        if (content) {
          container.removeChild(content);
        }
      }
    };
  }, [fileName, previewData, containerRef, loadingRef]);

  return null; // This is a functional component that just handles the effect
};

interface DocumentPreviewProps {
  fileName?: string;
  filePath?: string;
  docId?: string;
  companyId?: string;
  onReplace?: () => void;
  isEditMode?: boolean;
  isReadOnly?: boolean;
  templateLibraryPreview?: boolean;
  templateLibraryMiniPreview?: boolean;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({ fileName, filePath, docId, companyId, onReplace, isEditMode = false, isReadOnly = false, templateLibraryPreview = false, templateLibraryMiniPreview = false }): JSX.Element => {
  const { features } = useFeatureFlags();
  const { user } = useAuth();
  const [processingAITask, setProcessingAITask] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [aiTaskError, setAiTaskError] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string>('');
  const excelContainerRef = useRef<HTMLDivElement>(null);
  const [excelViewerInstance, setExcelViewerInstance] = useState<any>(null);
  const [excelViewerLoading, setExcelViewerLoading] = useState(false);
  
  // DED-1131: Decode URL-encoded file names for display
  const displayFileName = useMemo(() => {
    if (!fileName) return '';
    try {
      return decodeURIComponent(fileName);
    } catch {
      return fileName;
    }
  }, [fileName]);

  // Inject styles for document preview
  useEffect(() => {
    // Inject styles for document previews
    const styleElement = document.createElement('style');
    styleElement.textContent = docStyles;
    document.head.appendChild(styleElement);

    return () => {
      // Clean up styles on unmount
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    loadPreview();

    // Cleanup function for Excel viewer
    return () => {
      if (excelViewerInstance) {
        // Cleanup if there's a destroy method (check documentation)
        // excelViewerInstance.destroy();
      }
    };
  }, [fileName, filePath, templateLibraryPreview, templateLibraryMiniPreview, companyId, docId]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required inputs
      if (!fileName) {
        setError('No document available for preview');
        setLoading(false);
        return;
      }

      console.log('Loading preview for:', { fileName, filePath, docId, companyId , templateLibraryPreview, templateLibraryMiniPreview})

      // Construct storage path
      const storagePath = templateLibraryPreview || templateLibraryMiniPreview
        ? `templates/templateLibrary/documents/${decodeURIComponent(fileName)}`
        : `customers/${companyId}/keyDocuments/otherDocs/uploaded/${docId}/${fileName}`;

      // Get preview data
      const data = await previewDocument(firebaseConfig, storageRef, storagePath, fileName, false);

      // Extract proper URL from the character indices returned by previewDocument
      // const extractUrl = (data: any): string => {
      //   if (typeof data === 'string') return data;
      //   // Reconstruct URL from individual characters in indexed properties
      //   let url = '';
      //   let i = 0;
      //   // Continue until we find a non-numeric key or run out of characters
      //   while (data[i] !== undefined) {
      //     url += data[i];
      //     i++;
      //   }
      //   return url;
      // };

      // // Create a proper object with a real URL string
      // const processedData = {
      //   ...data,
      //   url: extractUrl(data)
      // };

      setPreviewData(data);
      console.log('[DocumentPreview] Preview data:', data);

      // For text files, load content
      if (data.fileType === 'txt') {
        const content = await readTextFile(data.blob);
        setTextContent(content);
      }

      // Initialize Excel viewer for spreadsheets
      const initExcelViewer = async (data: any) => {
        if (!excelContainerRef.current || !data) return;

        console.log('Initializing Excel viewer in normal mode');
        setExcelViewerLoading(true); // Start loading

        try {
          // Clear any existing content and force proper dimensions
          excelContainerRef.current.style.width = '100%';
          excelContainerRef.current.style.height = '100%';
          excelContainerRef.current.innerHTML = '';

          // Get the data - either from blob or URL
          let fileData;

          if (data.blob) {
            // If we have a blob, read it as an ArrayBuffer
            console.log('Converting blob to ArrayBuffer for Excel viewer');
            fileData = await data.blob.arrayBuffer();
          } else if (data.url) {
            // If we have a URL, fetch the data
            console.log('Fetching Excel data from URL');
            const response = await fetch(data.url);
            const blob = await response.blob();
            fileData = await blob.arrayBuffer();
          } else {
            throw new Error('No blob or URL available for Excel file');
          }

          console.log('Excel data ready, size:', fileData.byteLength);

          // Short delay to ensure DOM is ready
          await new Promise(resolve => setTimeout(resolve, 200));

          // Initialize with the ArrayBuffer
          const viewer = new ExcelViewer(
            excelContainerRef.current,
            fileData, // Use ArrayBuffer data
            {
              theme: 'light',
              lang: 'en',
              themeBtn: true,
              row: {
                len: 20, // Set rows to show
                height: 25 // Set row height
              },
              col: {
                len: 10, // Set columns to show
                width: 100 // Set column width
              },
              size: 'contain' // Fit within container
            }
          );

          console.log('Excel viewer initialized successfully');
          // Update states
          setExcelViewerInstance(viewer);
          setExcelViewerLoading(false);
        } catch (error) {
          console.error('Failed to initialize Excel viewer:', error);
          setError('Failed to initialize Excel viewer');
          setExcelViewerLoading(false);
        }
      };

      if (data.fileType === 'xlsx' || data.fileType === 'csv') {
        await initExcelViewer(data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading preview:', error);
      setError('Failed to load document preview');
      setLoading(false);
    }
  };


  const handleFullScreen = async () => {
    try {
      if (!previewData) {
        await loadPreview();
      }

      if (!previewData) {
        throw new Error('Failed to load preview data');
      }

      // Generate appropriate full-screen content based on file type
      let fullScreenContent = '';

      switch (previewData.fileType) {
        case 'image':
          fullScreenContent = `<img src="${previewData.url}" style="max-width: 100%; max-height: 90vh; object-fit: contain;" />`;
          break;
        case 'pdf':
          // Use object tag instead of iframe for PDFs to prevent the page from loading in the frame
          // The object tag is more appropriate for PDF content
          fullScreenContent = `<object data="${previewData.url}" type="application/pdf" style="width: 100%; height: 90vh;">
            <p>It appears your browser doesn't support PDF viewing. <a href="${previewData.url}" target="_blank">Click here to download the PDF</a>.</p>
          </object>`;
          break;
        case 'txt':
          const content = textContent || await readTextFile(previewData.blob);
          fullScreenContent = `<pre style="background: white; color: black; padding: 20px; border-radius: 8px; width: 80%; max-height: 90vh; overflow: auto; white-space: pre-wrap;">${content}</pre>`;
          break;
        case 'docx':
          // Create container for DOCX content with mammoth.js
          fullScreenContent = `<div style="background: white; color: black; padding: 40px; border-radius: 8px; display: flex; flex-direction: column; align-items: center;">
            <div id="docx-content-container" style="width: 800px; min-height: 1000px; padding: 40px; border: 1px solid #ddd; box-shadow: 0 4px 8px rgba(0,0,0,0.1); text-align: left; margin-bottom: 20px;">
              <div id="loading-indicator" style="text-align: center; padding: 40px;">
                <div style="border: 6px solid #f3f3f3; border-top: 6px solid #0066cc; border-radius: 50%; width: 50px; height: 50px; margin: 0 auto 20px; animation: spin 2s linear infinite;"></div>
                <p>Loading document content...</p>
              </div>
            </div>
            <h3 style="margin: 20px 0 0; font-size: 24px;">${displayFileName}</h3>
            <div style="margin-top: 15px; display: flex; gap: 10px;">
              <a href="${previewData.url}" download="${displayFileName}" style="display: inline-block; background: #0066cc; color: white; padding: 10px 20px; text-decoration: none; border-radius: 4px;">Download Document</a>
            </div>
          </div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            /* Document content styling */
            #docx-content-container h1 { font-size: 24px; margin-top: 24px; margin-bottom: 16px; font-weight: bold; }
            #docx-content-container h2 { font-size: 20px; margin-top: 20px; margin-bottom: 14px; font-weight: bold; }
            #docx-content-container h3 { font-size: 18px; margin-top: 18px; margin-bottom: 12px; font-weight: bold; }
            #docx-content-container p { margin-bottom: 16px; line-height: 1.5; }
            #docx-content-container ul, #docx-content-container ol { margin-left: 20px; margin-bottom: 16px; }
            #docx-content-container li { margin-bottom: 8px; }
            #docx-content-container table { border-collapse: collapse; margin-bottom: 16px; width: 100%; }
            #docx-content-container table, #docx-content-container th, #docx-content-container td { border: 1px solid #ddd; padding: 8px; }
          </style>
          `;
          setTimeout(() => {
            // Fetch the document from the URL
            fetch(`${previewData.url}`)
              .then(response => response.arrayBuffer())
              .then(buffer => {
                // Load the mammoth.js library from CDN if needed
                if (typeof mammoth === 'undefined') {
                  console.log('Mammoth not loaded, showing fallback');
                  document.getElementById('docx-content-container').innerHTML =
                    '<div style="text-align: center; padding: 20px;"><p>Document viewer not available. Please download instead.</p></div>';
                  return;
                }

                // Extract the HTML from the DOCX
                console.log('Converting DOCX to HTML');
                mammoth.convertToHtml({ arrayBuffer: buffer })
                  .then(result => {
                    const html = result.value;
                    const warnings = result.messages;

                    // Display the HTML
                    document.getElementById('docx-content-container').innerHTML = html;

                    // Log any warnings
                    if (warnings.length > 0) {
                      console.log('Warnings during DOCX conversion:', warnings);
                    }
                  })
                  .catch(error => {
                    console.error('Error converting DOCX:', error);
                    document.getElementById('docx-content-container').innerHTML =
                      '<div style="text-align: center; padding: 20px;"><p>Failed to convert document. Please download instead.</p></div>';
                  });
              })
              .catch(error => {
                console.error('Error fetching DOCX:', error);
                document.getElementById('docx-content-container').innerHTML =
                  '<div style="text-align: center; padding: 20px;"><p>Failed to load document. Please download instead.</p></div>';
              });
          }, 500); // Small delay to ensure DOM is ready
          break;
        case 'xlsx':
        case 'csv':
          // Create a container for the Excel viewer in fullscreen mode
          fullScreenContent = `
            <div id="excel-fullscreen-container" style="width: 100%; height: 90vh; background: white; overflow: hidden;">
              <div id="excel-viewer-fs" style="width: 100%; height: 100%;"></div>
            </div>
          `;
          // init excel viewer
          // Load Excel viewer library if needed
          setTimeout(() => {
            // Initialize Excel viewer in fullscreen mode
            if (ExcelViewer) {
              // Use the blob URL to create a new viewer instance
              fetch(`${previewData.url}`)
                .then(response => response.blob())
                .then(blob => {
                  console.log('Got blob data, size:', blob.size);
                  // Convert blob to ArrayBuffer for Excel viewer
                  return blob.arrayBuffer();
                })
                .then(arrayBuffer => {
                  console.log('Created ArrayBuffer, size:', arrayBuffer.byteLength);
                  const excelViewerFS = new ExcelViewer(
                    document.getElementById('excel-viewer-fs'),
                    arrayBuffer, // Pass the ArrayBuffer data instead of blob
                    {
                      theme: 'light',
                      lang: 'en',
                      themeBtn: false
                    }
                  );
                  console.log('Excel viewer initialized successfully in fullscreen');
                })
                .catch(error => {
                  console.error('Failed to load Excel file in fullscreen:', error);
                  document.getElementById('excel-viewer-fs').innerHTML =
                    `<div style="text-align: center; padding: 20px;"><p>Failed to load Excel file. Please download instead.</p>` +
                    `<a href="${previewData.url}" download="${displayFileName}" style="display: inline-block; background: #0066cc; color: white; padding: 10px 20px; margin-top: 15px; text-decoration: none; border-radius: 4px;">Download File</a></div>`;
                });
            } else {
              // Fallback if Excel viewer is not available
              document.getElementById('excel-viewer-fs').innerHTML =
                `<div style="text-align: center; padding: 20px;"><p>Excel viewer is not available. Please download the file instead.</p>` +
                `<a href="${previewData.url}" download="${displayFileName}" style="display: inline-block; background: #0066cc; color: white; padding: 10px 20px; margin-top: 15px; text-decoration: none; border-radius: 4px;">Download File</a></div>`;
            }
          }, 500); // Small delay to ensure DOM is ready
          break;
        default:
          // DED-1131: Use displayFileName for decoded file name display
          fullScreenContent = `<div style="background: white; color: black; padding: 40px; border-radius: 8px; text-align: center;">
            <div style="font-size: 48px; margin-bottom: 20px;"><FileText /></div>
            <h3 style="margin: 0; font-size: 24px;">${displayFileName}</h3>
            <p>Preview not available for this file type</p>
            </div>`;
      }

      // For Excel files, we need to ensure the ExcelViewer script is loaded in the fullscreen context
      if (previewData.fileType === 'xlsx' || previewData.fileType === 'csv') {
        const excelViewerScript = `
          <script src="https://unpkg.com/excel-viewer/dist/excel-viewer.min.js"></script>
          <link rel="stylesheet" href="https://unpkg.com/excel-viewer/dist/excel-viewer.min.css">
        `;
        fullScreenContent = excelViewerScript + fullScreenContent;
      }

      // Create full-screen preview
      createFullScreenPreview(fullScreenContent);
    } catch (error) {
      console.error('Error showing full screen preview:', error);
      setError('Failed to show full screen preview');
    }
  };

  // Render appropriate preview based on file type
  const renderPreview = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center p-8">
          <div className="spinner"></div>
          <p className="mt-4 text-sm text-gray-500">Loading preview...</p>
        </div>
      );
    }

    if (error || !previewData) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <FileText size={48} className="text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">{error || 'Preview not available'}</p>
        </div>
      );
    }

    switch (previewData.fileType) {
      case 'image':
        return <img src={previewData.url} alt={fileName} className="max-h-[300px] object-contain mx-auto" />;

      case 'pdf':
        return (
          <div className="w-full h-full bg-gray-100 dark:bg-neutral-800 rounded">
            <object
              data={previewData.url}
              type="application/pdf"
              className="w-full h-full rounded"
            >
              <p className="text-center p-4">It appears your browser doesn't support PDF viewing.
                <a href={previewData.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Click here to download the PDF</a>.
              </p>
            </object>
          </div>
        );

      case 'txt':
      case 'json':
        return (
          <div className="w-full h-full bg-gray-100 dark:bg-neutral-800 rounded overflow-auto p-4">
            <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {textContent || 'Loading text content...'}
            </pre>
          </div>
        );

      case 'docx':
        return (
          <div className="w-full h-full flex flex-col">
            <div className="w-full flex-1 bg-white dark:bg-neutral-800 border rounded shadow overflow-auto p-6" id="docx-preview-container">
              {/* Loading spinner */}
              <div className="flex flex-col items-center justify-center h-full" id="docx-loading">
                <div className="spinner mb-4"></div>
                <p className="text-sm text-gray-500">Loading document...</p>
              </div>
            </div>
            <div className="text-xs text-gray-500 text-center mt-1">Use "Full Screen" for a better reading experience</div>

            {/* Initialize mammoth.js for DOCX preview */}
            <DocxViewer
              fileName={fileName}
              previewData={previewData}
              containerRef="docx-preview-container"
              loadingRef="docx-loading"
            />
          </div>
        );

      case 'xlsx':
      case 'csv':
        return (
          <div className="w-full h-full flex flex-col">
            <div className="w-full flex-1 bg-white dark:bg-neutral-800 border rounded overflow-hidden relative">
              <div ref={excelContainerRef} className="w-full h-full"></div>
              {excelContainerRef.current && excelViewerInstance ? (
                <div id="excel-fullscreen-container" style={{ width: '100%', height: '90vh', background: 'white', overflow: 'hidden' }}>
                  <div id="excel-viewer-fs" style={{ width: '100%', height: '100%' }}></div>
                </div>
              ) : (
                <div className="w-full h-full">
                  <div className="flex flex-row items-center justify-center">
                    <div className="spinner"></div>
                    <p className="text-sm text-gray-500 ml-3">Loading spreadsheet...</p>
                  </div>
                </div>
              )}
            </div>
            <div className="text-xs text-gray-500 text-center mt-1">Use "Full Screen" for a better viewing experience</div>
          </div>
          
        );

      default:
        return (
          <div className="flex flex-col items-center justify-center py-6">
            <FileText size={40} className="text-gray-400 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{fileName}</p>
            <p className="text-xs text-gray-500">Preview not available for this file type</p>
          </div>
        );
    }
  };

  return (
    <div className="w-full h-full bg-gray-100 dark:bg-neutral-800 rounded-lg overflow-hidden flex flex-col">
      <div className="preview-container flex-1 flex items-center justify-center overflow-hidden">
        {renderPreview()}
      </div>
      <div className="px-4 py-3 bg-gray-50 dark:bg-neutral-700 flex justify-between items-center">
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {!templateLibraryMiniPreview ? null :<span className="font-medium">{decodeURIComponent(fileName)}</span>}
        </div>
        <div className="flex gap-2">
          {isEditMode && !templateLibraryPreview && !templateLibraryMiniPreview && !isReadOnly && onReplace && (
            <button
              className="btn btn-outline btn-sm flex items-center dark:!text-white/80 dark:!border-white/30"
              onClick={onReplace}
            >
              <Upload className="h-4 w-4 mr-1" />
              Replace File
            </button>
          )}
          {!templateLibraryMiniPreview && (
            <button
              className="btn btn-outline btn-sm flex items-center dark:!text-white/80 dark:!border-white/30"
              onClick={handleFullScreen}
            >
              <Expand className="h-4 w-4 mr-1" />
              Full Screen
            </button>
          )}
        </div>
      </div>
      
      <AITaskProcessingDialog
        isOpen={aiDialogOpen}
        fileName={fileName || ''}
        onClose={() => {
          setAiDialogOpen(false);
          setAiTaskError(null);
        }}
      />
    </div>
  );
};

export default DocumentPreview;
